import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertV2, Button, Checkbox, Link, Typography } from '@getgo/chameleon-web-react-wrapper';

import { useAppSelector } from 'hooks';
import { isPaymentMethodPresent } from 'modules/payment-methods';
import { sessionBillingAddress } from 'modules/session-details';
import { DD_BECS_DETAILS } from 'utils/constants';
import st from 'utils/shared-translations';

import './direct-debit-becs-mandate.css';

interface DirectDebitBecsMandateProps {
  handleCancelMandate: any;
  accountNumber: string;
  branchCode: string;
  isSubmitting: boolean;
  isDefault: boolean;
  setDefault: (isDefault: boolean) => void;
  isValid: boolean;
  dirty: boolean;
}

const DirectDebitBecsMandate: FC<DirectDebitBecsMandateProps> = ({
  handleCancelMandate,
  accountNumber,
  branchCode,
  isSubmitting,
  isDefault,
  setDefault,
  isValid,
  dirty,
}): JSX.Element => {
  const intl = useIntl();

  const ctaRef = useRef<HTMLDivElement>(null);

  const [isChecked, setChecked] = useState(false);

  const selectedBillingAddress = useAppSelector(sessionBillingAddress);
  const selectedPaymentMethodIsPresent = useAppSelector(isPaymentMethodPresent);

  useEffect(() => {
    setTimeout(() => ctaRef.current?.scrollIntoView({ behavior: 'smooth' }), 1000);
  }, []);

  return (
    <>
      <div className="direct-debit-becs-mandate__info">
        <AlertV2 className="direct-debit-becs-mandate__alert">
          <FormattedMessage
            {...st['dd.sepa.mandate.gocardless.alert']}
            values={{
              link: (msg: ReactNode) => (
                <Link href="https://gocardless.com/legal/privacy/" target="_blank">
                  {msg}
                </Link>
              ),
            }}
          />
        </AlertV2>

        <Typography variant="body-small-strong">{intl.formatMessage(st['dd.becs.mandate.header'])}</Typography>
        <Typography variant="caption-small" color="type-color-secondary">
          {intl.formatMessage(st['dd.sepa.mandate.header.caption'])}
        </Typography>

        <div className="direct-debit-becs-mandate__user-details">
          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.account.holder.name'])}
          </Typography>
          <Typography variant="body-small">{`${selectedBillingAddress?.firstName} ${selectedBillingAddress?.lastName}`}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.company.name'])}
          </Typography>
          <Typography variant="body-small">{`${selectedBillingAddress?.firstName} ${selectedBillingAddress?.lastName}`}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.account.number'])}
          </Typography>
          <Typography variant="body-small">{accountNumber}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.branch.code'])}
          </Typography>
          <Typography variant="body-small">{branchCode}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.billing.address'])}
          </Typography>
          <Typography variant="body-small">
            {selectedBillingAddress.addressLine1},{' '}
            {selectedBillingAddress.addressLine2 ? `${selectedBillingAddress.addressLine2}, ` : ''}
            {selectedBillingAddress.city}, {selectedBillingAddress.state ? `${selectedBillingAddress.state}, ` : ''}
            {selectedBillingAddress.country} {selectedBillingAddress.postalCode}
          </Typography>
        </div>

        <div className="direct-debit-becs-mandate__creditor-details">
          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.creditor.name'])}
          </Typography>
          <Typography variant="body-small">{DD_BECS_DETAILS.creditorName}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.payment.type'])}
          </Typography>
          <Typography variant="body-small">{DD_BECS_DETAILS.paymentType}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.submission.date'])}
          </Typography>
          <Typography variant="body-small">
            {intl.formatDate(new Date(), {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.street.address'])}
          </Typography>
          <Typography variant="body-small">{DD_BECS_DETAILS.streetAddress}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.city'])}
          </Typography>
          <Typography variant="body-small">{DD_BECS_DETAILS.city}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.postal.code'])}
          </Typography>
          <Typography variant="body-small">{DD_BECS_DETAILS.postalCode}</Typography>

          <Typography variant="caption-medium" color="type-color-secondary">
            {intl.formatMessage(st['dd.sepa.mandate.country'])}
          </Typography>
          <Typography variant="body-small">{DD_BECS_DETAILS.Country}</Typography>
        </div>
      </div>

      <Checkbox className="direct-debit-becs-mandate__check-box" onChange={() => setChecked(!isChecked)}>
        {intl.formatMessage(st['dd.sepa.mandate.tnc'])}
      </Checkbox>

      <div className="direct-debit-becs-mandate__cta" ref={ctaRef}>
        {selectedPaymentMethodIsPresent && (
          <div className="direct-debit-becs-mandate__checkbox">
            <Checkbox
              className="direct-debit-becs-mandate__tnc-checkbox"
              disabled={isSubmitting}
              onClick={() => setDefault(!isDefault)}
              checked={isDefault}
            >
              {intl.formatMessage(st['payment.method.set.as.default'])}
            </Checkbox>
          </div>
        )}
        <div className="direct-debit-becs-mandate__cta-buttons">
          <Button
            className="direct-debit-becs-mandate__cancel-cta"
            variant="tertiary"
            disabled={isSubmitting}
            onClick={() => handleCancelMandate(false)}
          >
            {intl.formatMessage(st['dd.mandate.cta.cancel'])}
          </Button>
          <Button
            disabled={!isChecked || !isValid || isSubmitting || !dirty}
            isLoading={isSubmitting}
            variant="primary"
            type="submit"
          >
            {intl.formatMessage(st['dd.mandate.cta.save'])}
          </Button>
        </div>
      </div>
    </>
  );
};

export default DirectDebitBecsMandate;
